import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { END_SESSION_NOTE, getEndSessionBtNote } from '../../../shared/Helpers/constants';
import { CustomAlert, CustomButton, Text } from '../../../shared/uiComponents';
import { useCheckPermission } from '../../../shared/Helpers/hooks';
import { ButtonWrapper } from '../../../components/StyledComponents';
import { AdminTypes } from '../../../components/Menu';
import { PERMISSIONS } from '../../../App/constants';
import { useCanEditSOAPNote, useDurationIncreased, usePostSubmitEditMode, useSessionBillingStatus } from '../helpers';
import {
	DurationIncreaseMBHWarningDialog,
	DurationIncreaseWarningDialog,
	MBHBillingWarningDialog,
} from './postSubmitDialogs';
import ForcedOperation from './forcedOperation';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { checkWeeklyHoursValidation, clearWeeklValidationDetails } from '../../../redux/State/clientSlice/clientSlice';
import { isDoeFunder } from '../../../redux/API/constants';

const Content = ({
	onClose,
	collectDataHandler,
	submit,
	closeBeforeSubmit,
}: {
	closeBeforeSubmit: boolean;
	collectDataHandler: () => void;
	onClose: () => void;
	submit: () => void;
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const [params] = useSearchParams();
	const clientId = params.get('clientId');
	const { date, startTime, endTime, sessionType } = useSelector((state) => state.session.sessionInfo);
	const userId = useSelector((state) => state.soapNote.soapNote?.user?.id);
	const weeklyHoursValidation = useSelector((state) => state.client.weeklyHoursValidationDetails);
	const role = useSelector((state) => state.account.role);
	const isBCBA = role?.section.id === AdminTypes.bcba;

	const submitHandler = () => {
		if (closeBeforeSubmit) onClose();
		submit();
	};

	useEffect(() => {
		if (!clientId || !userId || !sessionType || !isBCBA) return;
		const sessionTypeId = sessionType.id;
		dispatch(
			checkWeeklyHoursValidation({
				clientId,
				userId,
				sessionTypeId,
				sessionDate: date,
				startTime,
				endTime,
			}),
		);
	}, [clientId, userId, date, startTime, endTime, sessionType, isBCBA, dispatch]);

	return (
		<div style={{ textAlign: 'center' }}>
			<Text title={END_SESSION_NOTE} className="padding16" type="psm" fontWeight="semiBold" />
			{sessionType.type === 3 && ( // Behavior Treatment
				<Text title={getEndSessionBtNote(isDoeFunder(sessionType.funderId))} className="padding16" type="psm" fontWeight="semiBold" />
			)}
			{isBCBA && weeklyHoursValidation && weeklyHoursValidation.wouldExceedWeeklyLimit && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
					}}
				>
					<Text title="Weekly Hour Limit Exceeded" type="pmd" fontWeight="semiBold" />
					<Text title={`Weekly Allocation: ${weeklyHoursValidation.weeklyAllocation} hours`} type="pxs" />
					<Text title={`Already Billed This Week: ${weeklyHoursValidation.alreadyBilledThisWeek} hours`} type="pxs" />
					<Text
						title={`This Session: ${(
							weeklyHoursValidation.totalThisWeek - weeklyHoursValidation.alreadyBilledThisWeek
						).toFixed(2)} hours`}
						type="pxs"
					/>
					<Text
						title={`Total This Week: ${weeklyHoursValidation.totalThisWeek} hours (${weeklyHoursValidation.overageHours} hours over limit)`}
						type="psm"
						fontWeight="semiBold"
					/>
					<Text title={`Monthly Hours Remaining: ${weeklyHoursValidation.monthlyHoursRemaining} hours`} type="pxs" />
					<Text
						title="Exceeding weekly allocations may affect session pacing and could impact insurance authorization compliance."
						type="pxs"
					/>
					<Text title="Do you want to submit this session anyway?" type="psm" fontWeight="medium" />
				</div>
			)}
			<ButtonWrapper>
				<CustomButton title="Edit Session Data" onClick={collectDataHandler} buttonType="outlined" />
				<CustomButton
					title="Submit"
					onClick={submitHandler}
					// loading={soapNoteLoading || sessionLoading}
				/>
				<CustomButton title="Cancel" buttonType="secondaryGray" onClick={onClose} />
			</ButtonWrapper>
		</div>
	);
};

const SoapNoteButtons = ({
	closeConfirmationOnSubmit = false,
	save,
	submit,
}: {
	closeConfirmationOnSubmit?: boolean;
	save: () => void;
	submit: () => void;
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch<DispatchProperties>();
	const isUpdating = useSelector((state) => state.soapNote.isUpdating);
	const [params] = useSearchParams();
	const CREATE = useCheckPermission(PERMISSIONS.SOAPNOTE.CREATE);
	const EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.EDIT);
	const POST_SUBMIT_EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.POST_SUBMIT_EDIT);

	const { isPostSubmitEditMode, isBcbaPostSubmitEditMode } = usePostSubmitEditMode();
	const { requiresConfirmation } = useSessionBillingStatus();
	const durationIncreased = useDurationIncreased();

	const [open, setOpen] = useState<boolean>(false);
	const [billingWarningOpen, setBillingWarningOpen] = useState<boolean>(false);
	const [durationWarningOpen, setDurationWarningOpen] = useState<boolean>(false);
	const [durationMBHWarningOpen, setDurationMBHWarningOpen] = useState<boolean>(false);

	const soapNoteLoading = useSelector((state) => state.soapNote.loading);
	const sessionLoading = useSelector((state) => state.session.loading);

	const canEdit = useCanEditSOAPNote();

	if (!canEdit && !isPostSubmitEditMode) return null;

	const collectDataHandler = () => {
		const sessionId = params.get('sessionId');
		const clientId = params.get('clientId');
		setOpen(false);
		navigate(`/calendar/session/${sessionId}/${clientId}`);
	};

	const closeHandler = () => {
		dispatch(clearWeeklValidationDetails());
		setOpen(false);
	};

	const handlePostSubmitSubmit = () => {
		if (durationIncreased) {
			if (requiresConfirmation) {
				setDurationMBHWarningOpen(true);
			} else {
				setDurationWarningOpen(true);
			}
		} else if (requiresConfirmation) {
			setBillingWarningOpen(true);
		} else {
			submit();
		}
	};

	const handleBillingWarningConfirm = () => {
		setBillingWarningOpen(false);
		submit();
	};

	const handleDurationIncreaseConfirm = () => {
		setDurationWarningOpen(false);
		setDurationMBHWarningOpen(false);
		submit();
	};

	if (isPostSubmitEditMode) {
		return (
			<>
				<ButtonWrapper>
					{(POST_SUBMIT_EDIT.permissionGranted || isBcbaPostSubmitEditMode) && (
						<CustomButton loading={isUpdating} title="Submit" className="marginTop8" onClick={handlePostSubmitSubmit} />
					)}
				</ButtonWrapper>
				<ButtonWrapper>
					<CustomButton title="Collect Data" onClick={collectDataHandler} loading={soapNoteLoading || sessionLoading} />
				</ButtonWrapper>
				<MBHBillingWarningDialog
					open={billingWarningOpen}
					onClose={() => setBillingWarningOpen(false)}
					onConfirm={handleBillingWarningConfirm}
					loading={soapNoteLoading || sessionLoading}
				/>
				<DurationIncreaseWarningDialog
					open={durationWarningOpen}
					onClose={() => setDurationWarningOpen(false)}
					onConfirm={handleDurationIncreaseConfirm}
					loading={soapNoteLoading || sessionLoading}
				/>
				<DurationIncreaseMBHWarningDialog
					open={durationMBHWarningOpen}
					onClose={() => setDurationMBHWarningOpen(false)}
					onConfirm={handleDurationIncreaseConfirm}
					loading={soapNoteLoading || sessionLoading}
				/>
			</>
		);
	}

	return (
		<>
			<ButtonWrapper>
				{EDIT.permissionGranted && (
					<CustomButton
						title="Save as draft"
						onClick={() => {
							toast('Saved');
							save();
							navigate('/calendar');
						}}
						className="marginTop8"
						buttonType="secondaryGray"
					/>
				)}
				{CREATE.permissionGranted && (
					<CustomButton loading={isUpdating} title="Submit" className="marginTop8" onClick={() => setOpen(true)} />
				)}
			</ButtonWrapper>
			<ButtonWrapper>
				{(CREATE.permissionGranted || EDIT.permissionGranted) && (
					<CustomButton title="Collect Data" onClick={collectDataHandler} loading={soapNoteLoading || sessionLoading} />
				)}
			</ButtonWrapper>
			<ForcedOperation submit={submit} />

			<CustomAlert
				isBusy={isUpdating}
				open={open}
				title="Attention"
				onClose={closeHandler}
				Content={({ onClose }) => (
					<Content
						onClose={onClose}
						collectDataHandler={collectDataHandler}
						submit={submit}
						closeBeforeSubmit={closeConfirmationOnSubmit}
					/>
				)}
			/>
		</>
	);
};

export default SoapNoteButtons;
